import { Injectable } from "@angular/core";
import { take } from "rxjs";
import { IRestLayerRequest } from "@gtmhub/core";
import { ITeam } from "@gtmhub/teams";
import { IUser, getCurrentUserId } from "@gtmhub/users";
import { AssigneesFacade } from "@webapp/assignees/services/assignees/assignees-facade.service";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { PluginStateService } from "@webapp/plugins/components/plugin/services/plugin-state.service";
import { PluginService } from "@webapp/plugins/components/plugin/services/plugin.service";
import { GetRequest, PluginPayload } from "@webapp/plugins/plugins.models";
import { PluginsFacade } from "@webapp/plugins/services/plugins-facade.service";
import { mergeCustomFields, setParams } from "@webapp/plugins/utils";
import { TeamsFacade } from "@webapp/teams/services/teams-facade.service";
import { UsersFacade } from "@webapp/users/services/users-facade.service";

@Injectable()
export class PluginPeopleService extends PluginService {
  constructor(
    private assigneesFacade: AssigneesFacade,
    private usersFacade: UsersFacade,
    private broadcastService: BroadcastService,
    private teamsFacade: TeamsFacade,
    private pluginsFacade: PluginsFacade,
    pluginStateService: PluginStateService,
    destroy$: DestroyService
  ) {
    super(pluginStateService, destroy$);
  }

  public getAssigneesByIds(type: string, data: PluginPayload<string[]>): void {
    const assigneesIdMap = this.assigneesFacade.getAssigneesIdMap();
    const assignees = data.payload.reduce(
      (payload, id) => {
        const assignee = assigneesIdMap[id];

        if (assignee) {
          payload.items.push(assignee);
          payload.totalCount = payload.items.length;
        }

        return payload;
      },
      { items: [], totalCount: 0 }
    );

    this.postMessage(assignees, type);
  }

  public getAssigneeById(type: string, data: GetRequest): void {
    const assigneesIdMap = this.assigneesFacade.getAssigneesIdMap();
    this.postMessage(assigneesIdMap[data.id], type);
  }

  public getUser(type: string, data: GetRequest): void {
    this.usersFacade
      .getUser$(data.id, { includeShadowedRoles: true }, setParams(data.pluginSettings.pluginId))
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public getUsers(type: string, data: PluginPayload<IRestLayerRequest>): void {
    this.usersFacade
      .getUsersV2$(
        {
          filter: data.payload.filter,
          fields: data.payload.fields.split(","),
          limit: data.payload.limit,
          page: data.payload.page,
          skip: data.payload.skip,
          sort: data.payload.sort.split(","),
        },
        setParams(data.pluginSettings.pluginId)
      )
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public getCurrentUser(type: string, data: GetRequest): void {
    this.usersFacade
      .getUser$(getCurrentUserId(), { includeShadowedRoles: true }, setParams(data.pluginSettings.pluginId))
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public createUser(type: string, data: PluginPayload<IUser>): void {
    this.usersFacade
      .createUser$(data.payload, { skipEmail: false }, setParams(data.pluginSettings.pluginId))
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
          this.broadcastService.emit("invitedUsers", [response]);
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public updateUser(type: string, data: PluginPayload<IUser>): void {
    this.usersFacade
      .getUser$(data.payload.id)
      .pipe(take(1))
      .subscribe((user) => {
        const existingCustomFields = user.customFields;
        const newCustomFields = data.payload.customFields;

        /** ensure to merge both objects, otherwise it will omit the customfields not specified in palyoad */
        if (newCustomFields) {
          data.payload.customFields = mergeCustomFields(existingCustomFields, newCustomFields);
        }
        this.usersFacade
          .patchUser$(data.payload.id, data.payload, setParams(data.pluginSettings.pluginId))
          .pipe(take(1))
          .subscribe({
            next: (response) => {
              this.postMessage(response, type);
              this.broadcastService.emit("usersChanged", true);
            },
            error: (error) => {
              this.postMessage(this.pluginsFacade.transformHttpError(error), type);
            },
          });
      });
  }

  public deleteUser(type: string, data: GetRequest): void {
    this.usersFacade
      .deleteUserById$(data.id, setParams(data.pluginSettings.pluginId))
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
          this.broadcastService.emit("userDeleted", { id: data.id });
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public createTeam(type: string, data: PluginPayload<ITeam>): void {
    this.teamsFacade
      .createTeam$(data.payload, setParams(data.pluginSettings.pluginId))
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
          this.broadcastService.emit("teamCreated", { response });
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public updateTeam(type: string, data: PluginPayload<ITeam>): void {
    this.teamsFacade
      .getTeam$(data.payload.id)
      .pipe(take(1))
      .subscribe((team) => {
        const existingCustomFields = team.customFields;
        const newCustomFields = data.payload.customFields;

        /** ensure to merge both objects, otherwise it will omit the customfields not specified in palyoad */
        if (newCustomFields) {
          data.payload.customFields = mergeCustomFields(existingCustomFields, newCustomFields);
        }
        this.teamsFacade
          .updateTeam$(data.payload, setParams(data.pluginSettings.pluginId))
          .pipe(take(1))
          .subscribe({
            next: (response) => {
              this.postMessage(response, type);
              this.broadcastService.emit("teamUpdated", { response });
            },
            error: (error) => {
              this.postMessage(this.pluginsFacade.transformHttpError(error), type);
            },
          });
      });
  }

  public deleteTeam(type: string, data: GetRequest): void {
    this.teamsFacade
      .deleteTeamById$(data.id, setParams(data.pluginSettings.pluginId))
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
          this.broadcastService.emit("teamDeleted", { teamId: data.id });
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }
}
